import { cn } from "@/lib/utils"

export interface OrderFinancialSummaryRow {
  label: string
  amount: number
  highlight?: boolean
}

export interface OrderFinancialSummarySection {
  key: string | number
  title?: string
  rows: OrderFinancialSummaryRow[]
  accent?: "amber" | "primary"
}

interface OrderFinancialSummaryProps {
  title: string
  totalAmount: number
  rows?: OrderFinancialSummaryRow[]
  sections?: OrderFinancialSummarySection[]
  className?: string
}

function formatCurrency(amount: number) {
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function SummaryRows({
  rows,
  accent = "primary",
}: {
  rows: OrderFinancialSummaryRow[]
  accent?: OrderFinancialSummarySection["accent"]
}) {
  return (
    <>
      {rows.map((row) => (
        <div
          key={row.label}
          className={cn(
            "flex justify-between gap-4 text-xs text-muted-foreground",
            row.highlight &&
              "border-t border-border/50 pt-2 font-black uppercase tracking-widest text-foreground"
          )}
        >
          <span>{row.label}</span>
          <span
            className={cn(
              "font-bold text-foreground",
              row.highlight && accent === "amber" && "text-amber-700 dark:text-amber-400",
              row.highlight && accent === "primary" && "text-primary"
            )}
          >
            {formatCurrency(row.amount)}
          </span>
        </div>
      ))}
    </>
  )
}

export function OrderFinancialSummary({
  title,
  totalAmount,
  rows = [],
  sections = [],
  className,
}: OrderFinancialSummaryProps) {
  return (
    <div className={cn("flex flex-col border-r border-border p-6", className)}>
      <span className="label-industrial mb-4 text-muted-foreground/60">{title}</span>
      <span className="font-display text-[2.125rem] font-black leading-none tracking-tight text-primary">
        {formatCurrency(totalAmount)}
      </span>

      {rows.length > 0 && (
        <div className="mt-6 space-y-2 border-t border-border/50 pt-4">
          <SummaryRows rows={rows} />
        </div>
      )}

      {sections.map((section) => (
        <div key={section.key} className="mt-4 space-y-1.5 border-t border-amber-500/30 pt-4">
          {section.title && (
            <p className="mb-2 text-xs font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">
              {section.title}
            </p>
          )}
          <SummaryRows rows={section.rows} accent={section.accent ?? "amber"} />
        </div>
      ))}
    </div>
  )
}
